// Hand-maintained — override generation
import {
  getAdminStorageStats,
  getAdminBuckets,
  getAdminBucketsById,
  getAdminBucketsByIdStats,
  getAdminStorageFiles,
  getAdminStorageFilesById,
  patchAdminStorageFilesByIdArchive,
  patchAdminStorageFilesByIdRestore,
  patchAdminStorageFilesByIdConfirmUpload,
  patchAdminStorageFilesByIdSoftDelete,
  patchAdminStorageFilesByIdTag,
  patchAdminStorageFilesByIdUpdateMetadata,
  getAdminStorageFilesByChecksum,
  getAdminStorageFilesChildren,
  getAdminStorageRecommendations,
  getAdminStorageRecommendationsPending,
  patchAdminStorageRecommendationsByIdAccept,
  patchAdminStorageRecommendationsByIdDismiss,
} from "../_internal/sdk.gen";
import type {
  Bucket,
  StorageFile,
  StorageRecommendation,
  StorageStats,
} from "../_internal/types.gen";
import type { RequestOptions } from "../base-client";
import { RequestBuilder } from "../request-builder";

/**
 * Platform-level storage management.
 *
 * Provides access to global storage statistics and bucket administration.
 * Buckets are provisioned in GCS (production) or MinIO (development).
 * Processing-type (internal) buckets are excluded from listings by default.
 */
export function createStorageNamespace(rb: RequestBuilder) {
  return {
    /**
     * Retrieves platform-wide storage statistics.
     *
     * Pass a `workspaceId` to scope results to a single workspace.
     *
     * @param workspaceId - Optional workspace ID to filter statistics.
     * @param options - Optional request options.
     * @returns Storage statistics record with byte counts and file totals.
     */
    stats: async (
      workspaceId?: string,
      options?: RequestOptions,
    ): Promise<StorageStats[]> => {
      const params = workspaceId
        ? { query: { "filter[workspace_id]": workspaceId } }
        : {};
      return rb.execute<StorageStats[]>(getAdminStorageStats, params, options);
    },

    buckets: {
      /**
       * Lists all storage buckets, excluding processing-type buckets by default.
       *
       * @param options - Optional request options.
       * @returns Array of Bucket objects.
       */
      list: async (options?: RequestOptions): Promise<Bucket[]> => {
        return rb.execute<Bucket[]>(getAdminBuckets, {}, options);
      },

      /**
       * Fetches a single bucket by ID.
       *
       * @param id - Bucket ID.
       * @param options - Optional request options.
       * @returns The Bucket record.
       */
      get: async (id: string, options?: RequestOptions): Promise<Bucket> => {
        return rb.execute<Bucket>(
          getAdminBucketsById,
          { path: { id } },
          options,
        );
      },

      /**
       * Fetches a bucket with the `storage_used` calculation.
       *
       * Returns total bytes across all workspace files stored in this bucket.
       *
       * @param id - Bucket ID.
       * @param options - Optional request options.
       * @returns Bucket record including computed `storage_used` field.
       */
      stats: async (id: string, options?: RequestOptions): Promise<Bucket> => {
        return rb.execute<Bucket>(
          getAdminBucketsByIdStats,
          { path: { id } },
          options,
        );
      },
    },

    /**
     * Sub-namespace for admin storage file operations.
     */
    files: {
      /** Lists storage files. */
      list: async (options?: RequestOptions): Promise<StorageFile[]> => {
        return rb.execute<StorageFile[]>(getAdminStorageFiles, {}, options);
      },

      /** Retrieves a single storage file by ID. */
      get: async (
        id: string,
        options?: RequestOptions,
      ): Promise<StorageFile> => {
        return rb.execute<StorageFile>(
          getAdminStorageFilesById,
          { path: { id } },
          options,
        );
      },

      /** Archives a file. */
      archive: async (
        id: string,
        options?: RequestOptions,
      ): Promise<StorageFile> => {
        return rb.execute<StorageFile>(
          patchAdminStorageFilesByIdArchive,
          { path: { id }, body: { data: { id, type: "storage-file" } } },
          options,
        );
      },

      /** Restores an archived file. */
      restore: async (
        id: string,
        options?: RequestOptions,
      ): Promise<StorageFile> => {
        return rb.execute<StorageFile>(
          patchAdminStorageFilesByIdRestore,
          { path: { id }, body: { data: { id, type: "storage-file" } } },
          options,
        );
      },

      /** Confirms a presigned upload. */
      confirmUpload: async (
        id: string,
        attributes?: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<StorageFile> => {
        return rb.execute<StorageFile>(
          patchAdminStorageFilesByIdConfirmUpload,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "storage-file",
                attributes: attributes ?? {},
              },
            },
          },
          options,
        );
      },

      /** Soft-deletes a file. */
      softDelete: async (
        id: string,
        options?: RequestOptions,
      ): Promise<StorageFile> => {
        return rb.execute<StorageFile>(
          patchAdminStorageFilesByIdSoftDelete,
          { path: { id }, body: { data: { id, type: "storage-file" } } },
          options,
        );
      },

      /** Replaces tags on a file. */
      tag: async (
        id: string,
        tags: string[],
        options?: RequestOptions,
      ): Promise<StorageFile> => {
        return rb.execute<StorageFile>(
          patchAdminStorageFilesByIdTag,
          {
            path: { id },
            body: {
              data: { id, type: "storage-file", attributes: { tags } },
            },
          },
          options,
        );
      },

      /** Merges new metadata into the file's existing metadata. */
      updateMetadata: async (
        id: string,
        newMetadata: Record<string, unknown>,
        options?: RequestOptions,
      ): Promise<StorageFile> => {
        return rb.execute<StorageFile>(
          patchAdminStorageFilesByIdUpdateMetadata,
          {
            path: { id },
            body: {
              data: {
                id,
                type: "storage-file",
                attributes: { new_metadata: newMetadata },
              },
            },
          },
          options,
        );
      },

      /** Finds files by checksum for deduplication. */
      byChecksum: async (
        checksum: string,
        options?: RequestOptions,
      ): Promise<StorageFile[]> => {
        return rb.execute<StorageFile[]>(
          getAdminStorageFilesByChecksum,
          { query: { checksum } },
          options,
        );
      },

      /** Lists child files for a parent ID. */
      listChildren: async (
        parentId: string,
        options?: RequestOptions,
      ): Promise<StorageFile[]> => {
        return rb.execute<StorageFile[]>(
          getAdminStorageFilesChildren,
          { query: { parent_id: parentId } },
          options,
        );
      },

      /**
       * Generate a short-lived presigned download URL for a file.
       *
       * @param id - The UUID of the file to download.
       * @param params - Optional parameters (e.g., custom `expires_in` seconds).
       * @param options - Optional request options.
       * @returns A promise resolving to the presigned URL and its expiry.
       */
      requestDownloadUrl: async (
        id: string,
        params?: { expires_in?: number },
        options?: RequestOptions,
      ): Promise<{ url: string; expires_at: string }> => {
        return rb.rawPost<{ url: string; expires_at: string }>(
          `/isv/storage-files/${id}/download-url`,
          params?.expires_in != null
            ? { data: { expires_in: params.expires_in } }
            : undefined,
          options,
        );
      },
    },

    /**
     * Sub-namespace for AI-generated storage recommendations.
     *
     * Recommendations include duplicate detection, stale file cleanup,
     * and retention policy improvements.
     */
    recommendations: {
      /** Lists all recommendations. */
      list: async (
        options?: RequestOptions,
      ): Promise<StorageRecommendation[]> => {
        return rb.execute<StorageRecommendation[]>(
          getAdminStorageRecommendations,
          {},
          options,
        );
      },

      /** Lists pending (unresolved) recommendations. */
      listPending: async (
        options?: RequestOptions,
      ): Promise<StorageRecommendation[]> => {
        return rb.execute<StorageRecommendation[]>(
          getAdminStorageRecommendationsPending,
          {},
          options,
        );
      },

      /** Accepts a recommendation. */
      accept: async (
        id: string,
        options?: RequestOptions,
      ): Promise<StorageRecommendation> => {
        return rb.execute<StorageRecommendation>(
          patchAdminStorageRecommendationsByIdAccept,
          {
            path: { id },
            body: { data: { id, type: "storage-recommendation" } },
          },
          options,
        );
      },

      /** Dismisses a recommendation. */
      dismiss: async (
        id: string,
        options?: RequestOptions,
      ): Promise<StorageRecommendation> => {
        return rb.execute<StorageRecommendation>(
          patchAdminStorageRecommendationsByIdDismiss,
          {
            path: { id },
            body: { data: { id, type: "storage-recommendation" } },
          },
          options,
        );
      },
    },
  };
}
